// A card in the thread: a Work Item read live from its Run, with the actions the person may take.
// Anything irreversible or outbound shows its preview first (the same one the Cockpit shows); an
// invoice can also be seen exactly as it prints. A failed step names the problem and the recovery. A BOM or routing
// LAMP drafted from a drawing is its Form, corrected and confirmed on the card. A Task's Done asks for what its Output
// Contract needs from the person; a Task LAMP can't record yet offers no Done and says where it is done. An order held
// on credit links the customer's overdue invoices, each opening as its PDF. A drafted order is confirmed with its
// delivery date, kept or corrected. A drawing under review shows the drawing, whose it is and what LAMP read, and its
// open questions are kept, edited, added to or removed before Confirm (#507). A material check waiting for a part's BOM
// offers "Give the BOM" to whoever may give it: the part's Form, recorded as they fill it in (#565). A press the server
// refused is said on the card itself, in plain words, with what was typed on it left as it stands (#1006).
import { useEffect, useRef, useState } from "react";
import type { Card, CardAction, CardForm, Failure } from "../api.js";
import { Dialog } from "../Dialog.js";
import { plainAmounts } from "../i18n/format.js";
import { useWords } from "../i18n/words.js";
import type { WordKey } from "../shell/nav.js";
import { PartBomForm } from "../items/PartBomForm.js";
import { ActionPreview } from "../preview/ActionPreview.js";
import { FailureNotice } from "../preview/Failure.js";
import { AskForm } from "./AskForm.js";
import { SetUpForm } from "./SetUpForm.js";

const TAGS: Record<Card["kind"], { label: WordKey; className: string }> = {
  ask: { label: "cardNeedsYou", className: "tag tag-need" },
  do: { label: "cardYourTask", className: "tag tag-need" },
  blocked: { label: "cardBlocked", className: "tag tag-bad" },
  working: { label: "cardWorking", className: "tag tag-plain" },
  done: { label: "cardDone", className: "tag tag-good" },
};

/** The credit check (3.6.3.1): an order held on credit is released by the Owner, for this order, with a reason. */
const CREDIT_CHECK = "3.6.3.1";

/** What "done" says on a Task card, named for its own step (pack Tasks); any other Task's is plain. */
const DONE: Readonly<Record<string, WordKey>> = {
  "10.1.1.1": "cardPicked",
  "10.2.1.1": "cardHandedOver",
};

export interface CardViewProps {
  readonly card: Card;
  readonly busy: boolean;
  /**
   * `reason` when sending back; `text`, a message's words as the person confirmed them; `form`, a drafted BOM's or
   * routing's Form as the person corrected it.
   */
  readonly onAct: (card: Card, action: CardAction, input?: { reason?: string; text?: string; form?: CardForm }) => void;
  /** Opens "Give the BOM" at once, as Today does when it was pressed there. */
  readonly givingBom?: boolean;
  /** Why the server refused the person's last press on this card, said on it. */
  readonly refusal?: Failure | undefined;
}

export function CardView({ card, busy, onAct, givingBom: giving = false, refusal }: CardViewProps) {
  const w = useWords();
  const [printing, setPrinting] = useState(false);
  const [sendingBack, setSendingBack] = useState(false);
  const [givingBom, setGivingBom] = useState(giving);
  const reason = useRef<HTMLTextAreaElement>(null);
  const sendBackButton = useRef<HTMLButtonElement>(null);
  const askedWhy = useRef(false);
  // Focus follows the send-back form: into it when it opens, back to its button when it is cancelled.
  useEffect(() => {
    if (sendingBack) reason.current?.focus();
    else if (askedWhy.current) sendBackButton.current?.focus();
    askedWhy.current = sendingBack;
  }, [sendingBack]);
  const why = useRef<HTMLTextAreaElement>(null);
  const preview = card.preview;
  const invoice = preview?.kind === "invoice" ? preview : undefined;
  const releasing = card.task === CREDIT_CHECK && card.kind === "blocked" && card.actions.includes("confirm");
  const tag = TAGS[card.kind];
  const needsYou = card.actions.length > 0;
  // A BOM or routing is its own Form, confirmed (or, set up by hand, done) from it.
  const setUp = preview?.kind === "bom" || preview?.kind === "routing";
  // A material check waiting for a part's BOM is done by giving it, in its Form below.
  const givesBom = card.bom !== undefined && card.actions.includes("done");
  // A Done that asks for fields is its own Form below.
  const asking = card.actions.includes("done") && ((card.asks?.length ?? 0) > 0 || setUp || givesBom);
  // So is a drafted order's Confirm, with its delivery date to keep or correct.
  const confirmAsking = !asking && card.actions.includes("confirm") && (card.asks?.length ?? 0) > 0;

  // Everything offered beside Confirm.
  const others = (
    <>
      {card.actions.includes("done") && !asking ? (
        <button type="button" className="btn btn-go" disabled={busy} onClick={() => onAct(card, "done")}>
          {w[DONE[card.task] ?? "cardDoneIt"]}
        </button>
      ) : null}
      {card.actions.includes("send_back") && !sendingBack ? (
        <button ref={sendBackButton} type="button" className="btn" disabled={busy} onClick={() => setSendingBack(true)}>
          {w.cardSendBack}
        </button>
      ) : null}
      {invoice && card.status !== "done" ? (
        <button type="button" className="linkish" onClick={() => setPrinting(true)}>
          {w.cardSeePrint}
        </button>
      ) : null}
      {invoice?.document ? (
        <a className="btn" href={invoice.document.pdf} target="_blank" rel="noopener">
          {w.cardOpenPdf(invoice.number)}
        </a>
      ) : null}
    </>
  );

  return (
    <section className={needsYou ? "card need" : "card"} aria-label={card.name}>
      <header>
        <span className={tag.className}>{w[tag.label]}</span>
        <h3>{card.name}</h3>
      </header>
      {card.failure ? (
        <FailureNotice
          failure={card.failure}
          busy={busy}
          onRetry={card.actions.includes("retry") ? () => onAct(card, "retry") : undefined}
        />
      ) : null}
      {card.problems.length > 0 ? (
        <ul className="alert">
          {card.problems.map((p) => (
            <li key={p}>{plainAmounts(p)}</li>
          ))}
        </ul>
      ) : null}
      {card.email && card.status !== "done" ? (
        <figure className="email-preview" aria-label={w.cardEmailTo(card.email.counterparty)}>
          <figcaption className="small">
            {card.email.to ? (
              <>
                {w.cardEmailTo(card.email.counterparty)} <span translate="no">{card.email.to}</span>
              </>
            ) : (
              w.cardEmailNoAddress(card.email.counterparty)
            )}
          </figcaption>
          <p>
            <b>{card.email.subject}</b>
          </p>
          <blockquote className="message">{card.email.text}</blockquote>
        </figure>
      ) : null}
      {card.overdue && card.overdue.length > 0 ? (
        <ul className="overdue" aria-label={w.cardOverdue}>
          {card.overdue.map((d) => (
            <li key={d.pdf}>
              <a href={d.pdf} target="_blank" rel="noopener">
                {d.number}
              </a>
            </li>
          ))}
        </ul>
      ) : null}
      {card.files && card.files.length > 0 ? (
        <ul className="overdue" aria-label={w.cardFiles}>
          {card.files.map((f) => (
            <li key={f.href}>
              <a href={f.href} target="_blank" rel="noopener">
                {w.cardOpenFile(f.name)}
              </a>
            </li>
          ))}
        </ul>
      ) : null}
      {preview?.kind === "bom" || preview?.kind === "routing" ? (
        <SetUpForm
          preview={preview}
          canConfirm={card.actions.includes("confirm") || card.actions.includes("done")}
          busy={busy}
          onConfirm={(form) => onAct(card, card.actions.includes("confirm") ? "confirm" : "done", { form })}
        >
          {others}
        </SetUpForm>
      ) : preview ? (
        <ActionPreview
          preview={preview}
          canConfirm={card.actions.includes("confirm") && !confirmAsking}
          busy={busy}
          onConfirm={(text) => onAct(card, "confirm", text === undefined ? undefined : { text })}
        >
          {others}
        </ActionPreview>
      ) : releasing ? (
        <form
          className="send-back"
          onSubmit={(event) => {
            event.preventDefault();
            const reason = why.current?.value.trim();
            if (reason) onAct(card, "confirm", { reason });
            else why.current?.focus();
          }}
        >
          <label className="field">
            {w.cardReleaseWhy}
            <textarea ref={why} rows={2} maxLength={500} required />
          </label>
          <div className="acts">
            <button type="submit" className="btn btn-go" disabled={busy}>
              {w.cardRelease}
            </button>
            {others}
          </div>
        </form>
      ) : (
        <div className="acts">
          {card.actions.includes("confirm") && !confirmAsking ? (
            <button type="button" className="btn btn-go" disabled={busy} onClick={() => onAct(card, "confirm")}>
              {w.cardConfirm}
            </button>
          ) : null}
          {others}
        </div>
      )}
      {asking && card.asks && card.asks.length > 0 ? (
        <AskForm
          // A card read again with other fields (moved on, corrected elsewhere) starts its Form again from them: a
          // Form kept from the card as it was sends what the card no longer holds (#1006).
          key={JSON.stringify(card.asks)}
          asks={card.asks}
          done={w[DONE[card.task] ?? "cardDoneIt"]}
          busy={busy}
          onDone={(form) => onAct(card, "done", { form })}
        />
      ) : null}
      {givesBom && card.bom ? (
        givingBom ? (
          <PartBomForm
            form={card.bom}
            submitLabel={w.bomGive}
            busy={busy}
            onSubmit={(given) =>
              onAct(card, "done", {
                form: {
                  lines: given.lines.map((l) => ({ ...l })),
                  operations: given.operations.map((o) => ({ ...o })),
                },
              })
            }
          >
            <button type="button" className="btn" onClick={() => setGivingBom(false)}>
              {w.cardCancel}
            </button>
          </PartBomForm>
        ) : (
          <div className="acts">
            <button type="button" className="btn btn-go" disabled={busy} onClick={() => setGivingBom(true)}>
              {w.bomGive}
            </button>
          </div>
        )
      ) : null}
      {confirmAsking && card.asks ? (
        <AskForm
          key={JSON.stringify(card.asks)}
          asks={card.asks}
          done={w.cardConfirm}
          busy={busy}
          onDone={(form) => onAct(card, "confirm", { form })}
        />
      ) : null}
      {refusal ? <FailureNotice failure={refusal} /> : null}
      {card.elsewhere && card.status !== "done" ? <p className="muted">{card.elsewhere}</p> : null}
      {sendingBack ? (
        <form
          className="send-back"
          onSubmit={(event) => {
            event.preventDefault();
            const why = reason.current?.value.trim();
            onAct(card, "send_back", why ? { reason: why } : undefined);
          }}
        >
          <label className="field">
            {w.cardWhatChange}
            <textarea ref={reason} rows={2} maxLength={500} />
          </label>
          <div className="acts">
            <button type="submit" className="btn btn-go" disabled={busy}>
              {w.cardSendBack}
            </button>
            <button type="button" className="btn" onClick={() => setSendingBack(false)}>
              {w.cardCancel}
            </button>
          </div>
        </form>
      ) : null}
      {printing && invoice ? (
        <Dialog title={w.cardPrintTitle(invoice.number)} onClose={() => setPrinting(false)}>
          <iframe
            title={w.cardInvoice(invoice.number)}
            src={`/api/runs/${card.run}/work-items/${card.workItem}/invoice`}
            sandbox=""
          />
        </Dialog>
      ) : null}
    </section>
  );
}
